class CartDrawerUpgrade {
  constructor() {
    this.observeCartDrawer();
    this.init();
  }

  init() {
    const rows = document.querySelectorAll('tr.cart-item[data-upgrade-eligible="true"]');
    // console.log('init called, eligible rows found:', rows.length);
    rows.forEach(row => {
      this.renderUpgradeBox(row);
    });
  }

  observeCartDrawer() {
    const target = document.querySelector('#CartDrawer-CartItems');
    // console.log('observeCartDrawer called, target found:', !!target);
    if (!target) return;

    if (this.observer) this.observer.disconnect();

    this.observer = new MutationObserver((mutations) => {
      // console.log('MutationObserver fired, mutations:', mutations.length);
      const isOwnMutation = mutations.every(m =>
        [...m.addedNodes].every(n => n.classList?.contains('cart-item-upgrade-row')) ||
        [...m.removedNodes].every(n => n.classList?.contains('cart-item-upgrade-row'))
      );
      // console.log('isOwnMutation:', isOwnMutation);
      if (!isOwnMutation) this.init();
    });

    this.observer.observe(target, { childList: true, subtree: true });
  }

  renderUpgradeBox(row) {
    if (row.nextElementSibling?.classList.contains('cart-item-upgrade-row')) return;

    const variants = this.parseJSON(row.dataset.variants)?.sort((a, b) => a.price - b.price);
    const lensTypeOptions = this.parseJSON(row.dataset.lensTypeOptions);
    if (!variants || !lensTypeOptions) return;

    const currentVariantId = parseInt(row.dataset.currentVariantId);
    const currentPrice = parseInt(row.dataset.currentPrice);
    const currentLensType = row.dataset.currentLensType;
    const itemKey = row.dataset.itemKey;
    const currentQty = parseInt(row.dataset.currentQuantity);

    // Check sessionStorage to see if this row is currently in an upgraded state
    const originalVariantId = this.getUpgradeStateByCurrentVariant(currentVariantId);
    const isUpgraded = originalVariantId !== null;

    // Determine next variant based on lens type logic
    let nextVariant = null;
    let isFreeUpgrade = false;

    if (currentLensType === 'Non-Polarized') {
      // Always offer upgrade to Polarized specifically
      nextVariant = variants.find(v => v.lens_type === 'Polarized' && v.available && v.inventory_quantity >= currentQty);
      if (nextVariant) {
        isFreeUpgrade = nextVariant.price === currentPrice;
      }
    } else {
      // Standard logic: find next variant with a strictly higher price
      nextVariant = variants.find(v => v.price > currentPrice && v.available && v.inventory_quantity >= currentQty);
    }

    if (!nextVariant && !isUpgraded) return;

    const upgradeRow = document.createElement('tr');
    upgradeRow.classList.add('cart-item-upgrade-row');
    upgradeRow.dataset.forKey = itemKey;

    const td = document.createElement('td');
    td.setAttribute('colspan', '4');

    if (isUpgraded) {
      td.appendChild(this.buildUndoBox(row, upgradeRow, itemKey, originalVariantId, currentQty));
    } else {
      const priceDelta = nextVariant.price - currentPrice;
      const nextLensTypeOption = lensTypeOptions.find(o => o.lens_type === nextVariant.lens_type);
      const description = nextLensTypeOption?.description || '';
      const upgradePrefix = row.dataset.uiUpgradePrefix;
      const upgradeSuffix = row.dataset.uiUpgradeSuffix;
      const title = `${upgradePrefix} ${nextVariant.lens_type} ${upgradeSuffix}`;
      const upgradeBtnText = row.dataset.uiUpgradeBtn_text;

      td.appendChild(
        this.buildUpgradeBox(
          row, upgradeRow, title, description, priceDelta,
          itemKey, currentVariantId, currentQty, nextVariant, isFreeUpgrade
        )
      );
    }

    upgradeRow.appendChild(td);
    row.after(upgradeRow);
    row.classList.toggle('cart-item--upgraded', isUpgraded);
  }

  buildUpgradeBox(row, upgradeRow, title, description, priceDelta, itemKey, currentVariantId, currentQty, nextVariant, isFreeUpgrade) {
    const box = document.createElement('div');
    box.classList.add('cart-upgrade-box');

    const upgradePrefix = row.dataset.uiUpgradePrefix;
    const upgradeSuffix = row.dataset.uiUpgradeSuffix;
    const upgradeBtnText = row.dataset.uiUpgradeBtn_text;

    const priceDisplay = isFreeUpgrade
      ? (row.dataset.uiFreeUpgrade || 'Free Upgrade!')
      : `+${this.formatMoney(priceDelta)} per item`;

    box.innerHTML = `
      <div class="cart-upgrade-box__content">
        <div class="cart-upgrade-box__text">
          <h4 class="cart-upgrade-box__title">${title}</h4>
          <p class="cart-upgrade-box__description">${description}</p>
        </div>
        <div class="cart-upgrade-box__action">
          <span class="cart-upgrade-box__price${isFreeUpgrade ? ' cart-upgrade-box__price--free' : ''}">${priceDisplay}</span>
          <button class="cart-upgrade-box__button link" type="button">
            ${upgradeBtnText}
          </button>
        </div>
      </div>
    `;

    box.querySelector('.cart-upgrade-box__button').addEventListener('click', () => {
      this.doUpgrade(itemKey, currentVariantId, currentQty, nextVariant);
    });

    return box;
  }

  buildUndoBox(row, upgradeRow, itemKey, originalVariantId, currentQty) {
    const box = document.createElement('div');
    box.classList.add('cart-upgrade-box', 'cart-upgrade-box--undo');

    const undoText = row.dataset.uiUndo;
    const undoIcon = row.dataset.uiUndoIcon;

    box.innerHTML = `
      <div class="cart-upgrade-box__content cart-upgrade-box__content--undo">
        <button class="cart-upgrade-box__button cart-upgrade-box__button--undo" type="button">
          <span class="cart-upgrade-box__undo-icon"><svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5.8335 15.8333V14.1667H11.7502C12.6252 14.1667 13.3856 13.8889 14.0314 13.3333C14.6772 12.7778 15.0002 12.0833 15.0002 11.25C15.0002 10.4167 14.6772 9.72222 14.0314 9.16667C13.3856 8.61111 12.6252 8.33333 11.7502 8.33333H6.50016L8.66683 10.5L7.50016 11.6667L3.3335 7.5L7.50016 3.33333L8.66683 4.5L6.50016 6.66667H11.7502C13.0974 6.66667 14.2536 7.10417 15.2189 7.97917C16.1842 8.85417 16.6668 9.94444 16.6668 11.25C16.6668 12.5556 16.1842 13.6458 15.2189 14.5208C14.2536 15.3958 13.0974 15.8333 11.7502 15.8333H5.8335Z" fill="currentColor"/>
          </svg></span>
          <span class="link">${undoText}</span>
        </button>
      </div>
    `;

    box.querySelector('.cart-upgrade-box__button--undo').addEventListener('click', () => {
      this.doUndo(itemKey, originalVariantId, currentQty);
    });

    return box;
  }

  async doUpgrade(itemKey, currentVariantId, currentQty, nextVariant) {
    const upgradeRow = document.querySelector(`.cart-item-upgrade-row[data-for-key="${itemKey}"]`);
    this.setLoading(upgradeRow, true);

    // Save to sessionStorage before re-render wipes the DOM
    this.saveUpgradeState(currentVariantId, nextVariant.id);

    const success = await this.swapVariant(itemKey, currentQty, nextVariant.id);
    if (!success) {
      this.clearUpgradeState(currentVariantId);
      this.setLoading(upgradeRow, false);
      return;
    }

    await this.triggerDawnRerender();
    // MutationObserver fires init() after DOM updates
  }

  async doUndo(itemKey, originalVariantId, currentQty) {
    const upgradeRow = document.querySelector(`.cart-item-upgrade-row[data-for-key="${itemKey}"]`);
    this.setLoading(upgradeRow, true);

    const success = await this.swapVariant(itemKey, currentQty, originalVariantId);
    if (!success) {
      this.setLoading(upgradeRow, false);
      return;
    }

    // Clear state before re-render so init() renders upgrade box
    this.clearUpgradeState(originalVariantId);

    await this.triggerDawnRerender();
  }

  async swapVariant(itemKey, currentQty, newVariantId) {
    try {
      // Add the new variant first
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: newVariantId, quantity: currentQty }),
      });
      if (!addResponse.ok) throw new Error('Add failed');

      // Only remove the original once we know the add succeeded
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
      });
      if (!removeResponse.ok) throw new Error('Remove failed');

      return true;
    } catch (e) {
      console.error('CartDrawerUpgrade: swap failed', e);
      return false;
    }
  }

  async triggerDawnRerender() {
    try {
      const response = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const data = await response.json();

      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      if (drawerInner && data['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
        const newInner = parsed.querySelector('.drawer__inner');
        if (newInner) drawerInner.innerHTML = newInner.innerHTML;
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
        const newBubble = parsed.querySelector('.shopify-section');
        if (newBubble) bubble.innerHTML = newBubble.innerHTML;
      }

      // Re-attach observer to the newly rendered node, then init
      this.observeCartDrawer();
      this.init();

      this.clearAllUpgradeStatesIfCartEmpty();

    } catch (e) {
      console.error('CartDrawerUpgrade: rerender failed', e);
    }
  }

  // --- SessionStorage ---

  saveUpgradeState(originalVariantId, upgradedVariantId) {
    const state = this.getAllUpgradeStates();
    state[originalVariantId] = upgradedVariantId;
    sessionStorage.setItem('cartUpgradeState', JSON.stringify(state));
  }

  clearUpgradeState(originalVariantId) {
    const state = this.getAllUpgradeStates();
    delete state[originalVariantId];
    sessionStorage.setItem('cartUpgradeState', JSON.stringify(state));
  }

  clearAllUpgradeStates() {
    sessionStorage.removeItem('cartUpgradeState');
  }

  async clearAllUpgradeStatesIfCartEmpty() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      if (cart.item_count === 0) this.clearAllUpgradeStates();
    } catch (e) {
      console.error('CartDrawerUpgrade: cart check failed', e);
    }
  }

  // Given a current (upgraded) variant ID, return the original variant ID if found
  getUpgradeStateByCurrentVariant(currentVariantId) {
    const state = this.getAllUpgradeStates();
    const entry = Object.entries(state).find(([, upgraded]) => parseInt(upgraded) === currentVariantId);
    return entry ? parseInt(entry[0]) : null;
  }

  // Given an original variant ID, return the upgraded variant ID
  getUpgradeStateValue(originalVariantId) {
    return this.getAllUpgradeStates()[originalVariantId] ?? null;
  }

  getAllUpgradeStates() {
    try {
      return JSON.parse(sessionStorage.getItem('cartUpgradeState') || '{}');
    } catch {
      return {};
    }
  }

  // --- Utilities ---

  setLoading(upgradeRow, isLoading) {
    if (!upgradeRow) return;
    upgradeRow.classList.toggle('cart-upgrade-box--loading', isLoading);
    const btn = upgradeRow.querySelector('button');
    if (btn) btn.disabled = isLoading;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  parseJSON(str) {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error('CartDrawerUpgrade: failed to parse JSON', e);
      return null;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawerUpgrade = new CartDrawerUpgrade();
});

document.addEventListener('cart-drawer:rerendered', () => {
  // console.log('heard cart-drawer:rerendered!', window.cartDrawerUpgrade);
  setTimeout(() => {
    if (window.cartDrawerUpgrade) {
      window.cartDrawerUpgrade.observeCartDrawer();
      window.cartDrawerUpgrade.init();
    }
  }, 1000);
});